import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { MatInput, MatInputModule } from '@angular/material/input'
import { Observable, debounceTime, filter, map, startWith } from 'rxjs'

import { CommonModule } from '@angular/common'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { untilDestroyed } from '@app/shared/utils/utils'
import { SharedModule } from '@shared/shared.module'
import { sortBy } from 'lodash'

@Component({
  selector: 'scope-ui-autocomplete',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatSelectModule,
    SharedModule,
  ],
  templateUrl: './scope-ui-autocomplete.component.html',
  styleUrls: ['./scope-ui-autocomplete.component.scss'],
})
export class ScopeUiAutocompleteComponent implements OnInit, OnChanges {
  @Input() set initialValue(value: any) {
    this.selectedValue = value
    if (!this.ignoreNewValue) {
      this.control.setValue(value)
    }
    this.ignoreNewValue = false
    this.cdr.detectChanges()
  }

  @Input() set disabled(value: boolean) {
    if (value) this.control.disable()
    else this.control.enable()
  }

  _options!: any[]
  @Input() set options(value: any[]) {
    this._options = value
  }

  _selectedItems: any[] = []
  selectedItemsControl = new FormControl<string | any>('')
  get selectedItems(): any[] {
    return this._selectedItems
  }
  @Input() set selectedItems(value: any[]) {
    this._selectedItems = value || []
  }

  @Input() label!: string
  @Input() filterByAction?: boolean
  @Input() multiselect?: boolean
  @Input() isCheckboxMultiselect?: boolean
  @Input() showSelectedOptionsAmount?: boolean = this.isCheckboxMultiselect
  @Input() componentId!: string
  @Input() hasIcon = false
  @Input() optionClass?: string
  @Input() iconName!: string
  @Input() placeholder?: string
  @Input() markerProperty?: string
  @Input() markerText: string
  @Input() orderBy!: string
  @Input() applyFocusOut = false

  @Input() optionFn!: (value: any) => string
  @Input() optionDetailsFn!: (value: any) => string
  @Input() displayFn!: (value: any) => string

  @Input() noResultsMessage?: string = 'No results found'
  @Input() required = false
  @Input() enableDefaultOption = false
  @Input() hideToggleIcon = false
  @Input() showBreadcrumbs = false
  @Input() control = new FormControl<string | any>('')

  @Output() onSearch!: EventEmitter<{ searchString: string; event?: MatAutocompleteSelectedEvent }>
  @Output() onSelectDefaultOption!: EventEmitter<string>
  @Output() onSelectionChange!: EventEmitter<{ event: any; componentId?: any }>
  @Output() focusOut!: EventEmitter<{ event: any; componentId?: any }>

  @ViewChild(MatAutocompleteTrigger) matAutocompleteTrigger: MatAutocompleteTrigger
  @ViewChild(MatAutocomplete) matAutocomplete: MatAutocomplete
  @ViewChild(MatInput, { static: false }) input: MatInput

  selectedValue: any
  filteredOptions!: Observable<any[]>
  isFocused: boolean
  searchText: string
  ignoreNewValue = false

  constructor(private cdr: ChangeDetectorRef) {
    this.onSearch = new EventEmitter<any>()
    this.onSelectDefaultOption = new EventEmitter<string>()
    this.onSelectionChange = new EventEmitter<any>()
    this.focusOut = new EventEmitter<any>()
    this.destroy$ = untilDestroyed()
  }

  ngOnInit() {
    if (!this.displayFn) this.displayFn = (item: any) => (item && item.name ? item.name : '')
    this.cdr.detectChanges()
    if (this.filterByAction) this.autoCompleteByAction()
  }

  ngOnChanges() {
    if (!this.filterByAction) this.autoComplete()
  }

  resetAutoComplete() {
    this.control.reset()
  }

  onInput() {
    if (this.selectedValue) {
      this.selectedValue = null
      if (!this.multiselect) {
        this.ignoreNewValue = true
        this.onSelectionChange.emit({ event: undefined, componentId: this.componentId })
      }
    }
  }

  runSearch = (value) => {
    this.searchText = value
    this.onSearch.emit({ searchString: this.searchText })
  }

  autoComplete() {
    this.filteredOptions = this.control.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value) => {
        this.runSearch(value)
        let options = this.searchText
          ? this._filter(this.searchText as string)
          : this._sortWithUnassignedFirst(this._options)
        if (options?.length === 0) {
          options = [{ id: -1, displayName: 'No Results' }]
        }
        return options
      })
    )
  }

  autoCompleteByAction() {
    this.control.valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        filter((value) => typeof value === 'string'),
        map(this.runSearch)
      )
      .pipe(this.destroy$())
      .subscribe()
  }

  onSelection(event: MatAutocompleteSelectedEvent) {
    this.selectedValue = event.option.value

    if (this.selectedValue.id === -1) {
      this.onSelectDefaultOption.emit(this.searchText)
      this.control.setValue(this.searchText)
      return
    }

    this.onSelectionChange.emit({ event: this.selectedValue, componentId: this.componentId })

    if (this.filterByAction) this.onSearch.emit({ searchString: '', event })

    if (this.multiselect) this.control.setValue('')
  }

  private _getOptionValue(option: any): string {
    return (this.optionFn ? this.optionFn(option) : option.name || '').toLowerCase()
  }

  private _sortWithUnassignedFirst(options: any[]): any[] {
    if (!options) return options

    return [...options].sort((a, b) => {
      if (a.id === null) return -1
      if (b.id === null) return 1
      return this._getOptionValue(a).localeCompare(this._getOptionValue(b))
    })
  }

  private _filter(name: string): any[] {
    const filterValue = name.toLowerCase()
    const filtered = this._options?.filter((option) => this._getOptionValue(option).includes(filterValue))

    return filtered?.sort((a, b) => {
      if (a.id === null) return -1
      if (b.id === null) return 1

      const aValue = this._getOptionValue(a)
      const bValue = this._getOptionValue(b)
      const aIndex = aValue.indexOf(filterValue)
      const bIndex = bValue.indexOf(filterValue)

      return aIndex !== bIndex ? aIndex - bIndex : aValue.localeCompare(bValue)
    })
  }

  isMarkerPropertyAvailable(option: any): boolean {
    if (!this.markerProperty) {
      return false
    }
    if (typeof option[this.markerProperty] === 'function') {
      return option[this.markerProperty]()
    } else {
      return option[this.markerProperty]
    }
  }

  getOptionView(option: any) {
    if (option.id == -1) return `<span class="autocomplete-option">${this.noResultsMessage}</span>`

    if (option.getHtml) return option.getHtml(this.searchText)

    let value = this.optionFn ? this.optionFn(option) : option.name
    if (this.searchText) {
      const search = new RegExp(this.searchText, 'gi')
      value = value.replace(search, '<b>$&</b>')
    }

    if (this.showBreadcrumbs)
      return `<div class="option-container">
                <span class="autocomplete-option">${value}</span>
                <span class='autocomplete-breadcrumb'>${option.getBreadcrumb?.() || ''}</span>
              </div>`

    const marker =
      this.markerProperty && this.isMarkerPropertyAvailable(option)
        ? `<div class="option-marker"><span>${this.markerText}</span></div>`
        : ''
    if (marker)
      return `<span class="autocomplete-option">${value}</span>
            ${marker}`

    if (this.optionDetailsFn)
      return `<span class="autocomplete-option autocomplete-option-extended">
        <span class="autocomplete-option-label">${value}</span>
        <span class="autocomplete-option-details">${this.optionDetailsFn(option)}</span>
      </span>`

    return `<span class="autocomplete-option">${value}</span>`
  }

  clear($event?: MouseEvent) {
    $event?.stopPropagation()
    this.selectedValue = null
    this.control.setValue('')

    if ($event) {
      this.input.focus()
      if (this.isCheckboxMultiselect) this.input.value = ''
    }

    if (!this.multiselect && !this.isCheckboxMultiselect) {
      this.onSelectionChange.emit({ event: undefined, componentId: this.componentId })
    }
  }

  toggle($event: MouseEvent) {
    if (this.matAutocomplete.isOpen) {
      $event.preventDefault()
      $event.stopPropagation()
      this.matAutocompleteTrigger.closePanel()
    }
  }

  getSelectedItemNames() {
    return sortBy(this._selectedItems, 'name')
      ?.map((item) => item.name)
      ?.join(', ')
  }

  toggleSelection(option: any) {
    if (option?.id) {
      if (this.isSelected(option)) this._selectedItems.splice(this.getItemIndex(option), 1)
      else this._selectedItems.push(option)
      this.onSelectionChange.emit({ event: this._selectedItems })
      this.onFocusOut(false)
      if (!this.searchText?.length) this.input.value = this.getSelectedItemNames()
    }
  }

  onFocusOut(resetSearch = true, $event?: any) {
    if (this.applyFocusOut && !this.selectedValue) {
      const input = $event?.target as HTMLInputElement
      if (input) {
        this.onSelectionChange.emit({ event: input.value })
      }
    }
    if (this.isCheckboxMultiselect) {
      $event?.preventDefault()
      $event?.stopPropagation()
      this.isFocused = false

      if (this.searchText?.length && !resetSearch) this.input.value = this.searchText

      if (resetSearch) {
        this.input.value = this.getSelectedItemNames()
        this.clear()
      }
    }
  }

  isSelected = (option: any) => this.getItemIndex(option) !== -1
  getItemIndex = (option: any) => this._selectedItems.findIndex((item) => item === option)

  private readonly destroy$
}
